import React, { useEffect, useLayoutEffect, Suspense, lazy } from 'react';
import { Route, Routes, useLocation, useParams } from 'react-router-dom';
import { useRecoilState, useSetRecoilState } from 'recoil';
import TiktokPixel from 'tiktok-pixel';
import { Footer } from './Components/Footer/Footer';
import { Nav } from './Components/Nav/Nav';
import { ProtectedRoute } from './Components/ProtectedRoute/ProtectedRoute';
import { Spinner } from './Components/Spinner/Spinner';
import { useIsMobile } from './hooks/IsMobile';
import './index.css';
import { user, userLocation } from './store/user';
import { getUserLocations } from './utils/helpers';
import { waitForGoogleMaps } from './utils/googleMaps';

// Admin Panel
const AdminLogin = React.lazy(() =>
  import('./Pages/Admin/AdminLogin').then((m) => ({
    default: m.AdminLogin,
  }))
);
const AdminDashboard = React.lazy(() =>
  import('./Pages/Admin/AdminDashboard').then((m) => ({
    default: m.AdminDashboard,
  }))
);
const AdminProtectedRoute = React.lazy(() =>
  import('./Components/AdminProtectedRoute/AdminProtectedRoute').then((m) => ({
    default: m.AdminProtectedRoute,
  }))
);

// TikTok Pixel initialization flag
let pixelInitialized = false;

function App() {
  // Initialize TikTok Pixel only once
  useEffect(() => {
    if (!pixelInitialized) {
      TiktokPixel.init('CJRO313C77U5E7966T8G');
      pixelInitialized = true;
    }
  }, []);

  const location = useLocation();
  const params = useParams();
  const setUser = useSetRecoilState(user);
  const userData = useRecoilState(user);
  const setUserLocation = useSetRecoilState(userLocation);
  const isMobile = useIsMobile();

  useEffect(() => {
    const localUserData = localStorage.getItem('user');

    if (localUserData) {
      setUser(JSON.parse(localUserData));
    }

    getUserLocation();
  }, []);

  const getUserLocation = () => {
    // Check if the Geolocation API is available in the browser
    if ('geolocation' in navigator) {
      // Get the user's current position
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          // Extract the latitude and longitude from the position object
          const latitude = position.coords.latitude;
          const longitude = position.coords.longitude;

          // Wait for Google Maps API to be loaded
          await waitForGoogleMaps();
          performGeocode(latitude, longitude);
        },
        (error) => {
          setUserLocation(error);
        }
      );
    } else {
      // Geolocation is not available in this browser
      setUserLocation({
        code: 55,
        message: 'Geolocation is not supported in this browser.',
      });
    }
  };

  const performGeocode = (latitude, longitude) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder
      .geocode({ location: { lat: latitude, lng: longitude } })
      .then((response) => {
        if (response.results[0]) {
          const userLocation = getUserLocations(response.results);

          setUserLocation({
            lat: latitude,
            lng: longitude,
            cc: userLocation,
          });
        } else {
          console.log('No results found');
        }
      })
      .catch((e) => console.log('Geocoder failed due to: ' + e));
  };
  console.log(params);

  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <>
      <Suspense fallback={<Spinner />}>
        <Routes>
          {/* Admin Panel Routes */}
          <Route path="/" element={<AdminLogin />} />
          <Route
            path="/admin/dashboard"
            element={
              <Suspense fallback={<Spinner />}>
                <AdminProtectedRoute>
                  <AdminDashboard />
                </AdminProtectedRoute>
              </Suspense>
            }
          />
          <Route path="/admin" element={<AdminLogin />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
